import type {
  ContractSub, ContractOwner, SubcontractRequestListItem, ContractSubBucket, ContractSubStage, ContractWaitCategory,
  ContractAgreementType, ContractFormKey, ContractOwnerStep, ContractSubInput, ContractOwnerInput,
} from '@/lib/apiClient';
import { DATE_INPUT_MIN } from '@/lib/dates';

/**
 * What the Contracts screens are built from, kept free of React so the rules are
 * testable. The server decides where a subcontract stands (`bucket`, `missing`,
 * `clearedOn`); nothing here recomputes that. This file only arranges, filters and
 * labels what the API returned, and turns a form back into a request.
 */

// ── Vocabulary ───────────────────────────────────────────────────────────────

/** The order the weekly sheet lists them in, top to bottom. */
export const BUCKET_ORDER: readonly ContractSubBucket[] = [
  'preparing', 'internal_review', 'waiting_on_sub', 'signed', 'on_hold', 'missing_items', 'cleared', 'voided',
];

/**
 * The weekly sheet's own headings: its words AND its capitals, down to the all-caps
 * INDUSTRIAL & COMMERCIAL and CAN'T REPORT TO SITE. Only the sheet's stray spacing and
 * punctuation are tidied (spaces around the slashes, a dash for "Items-", a curly
 * apostrophe). Two differences are on purpose. The cleared heading leaves off
 * "(in past 30 days)": "Show older" puts older rows under it, and a job's own tab
 * lists every row it has. And "Voided" is not a heading on the sheet, which leaves a
 * voided row where it was, with a note. The API's `STAGE_LABELS` carries the five
 * stored stages for the activity feed — keep the two in step.
 */
export const BUCKET_LABELS: Record<ContractSubBucket, string> = {
  preparing: 'E&W Preparing Subcontract',
  internal_review: 'E&W Internal Review / Initializing',
  waiting_on_sub: 'Waiting On Sub',
  signed: 'Signed by Sub / Finalization in Process',
  on_hold: 'On Hold / In Review / Redlines Made',
  missing_items: 'Contract Finalized, Missing Items — CAN’T REPORT TO SITE',
  cleared: 'QUALIFIED SUBCONTRACTS — SUB IS CLEARED TO REPORT TO JOBSITE',
  voided: 'Voided',
};

export const STAGE_OPTIONS: readonly { key: ContractSubStage; label: string }[] = [
  { key: 'preparing', label: BUCKET_LABELS.preparing },
  { key: 'internal_review', label: BUCKET_LABELS.internal_review },
  { key: 'waiting_on_sub', label: BUCKET_LABELS.waiting_on_sub },
  { key: 'signed', label: BUCKET_LABELS.signed },
  { key: 'on_hold', label: BUCKET_LABELS.on_hold },
];

export const WAIT_CATEGORY_ORDER: readonly ContractWaitCategory[] = ['industrial_commercial', 'gc_only', 'fab'];
/** As the sheet spells the three "Waiting On Sub" groups, capitals included. */
export const WAIT_CATEGORY_LABELS: Record<ContractWaitCategory, string> = {
  industrial_commercial: 'INDUSTRIAL & COMMERCIAL',
  gc_only: 'GC Only',
  fab: 'FAB',
};

/** Short, as on the sheet and in the table. */
export const TYPE_LABELS: Record<ContractAgreementType, string> = {
  subcontract: 'Subcontract',
  psa: 'PSA',
  msa: 'MSA',
};
/** Spelled out, for the picker and for a tooltip. */
export const TYPE_NAMES: Record<ContractAgreementType, string> = {
  subcontract: 'Subcontract',
  psa: 'PSA — Professional Services Agreement',
  msa: 'MSA — Master Services Agreement',
};

/**
 * Whether this kind of agreement may be tracked with no job (#631): an MSA covers a vendor
 * across jobs, so it is not booked to one. Mirrors the API's `canGoWithoutJob`, which holds
 * the rule (and the table's CHECK behind it); this only asks before the round trip.
 */
export const canGoWithoutJob = (type: ContractAgreementType): boolean => type === 'msa';

export const FORM_SHORT: Record<ContractFormKey, string> = { cc: 'CC', nv: 'NV', w9: 'W9', coi: 'COI' };
export const FORM_LABELS: Record<ContractFormKey, string> = {
  cc: 'Contract signed and executed',
  nv: 'New vendor form',
  w9: 'W9 tax ID form',
  coi: 'Certificate of insurance',
};

export const OWNER_STEP_LABELS: Record<ContractOwnerStep, string> = {
  new: 'New',
  received: 'Received',
  redline_sent: 'Redline sent',
  approved: 'Approved',
  completed: 'Completed',
};

/** A job's Contracts tab. The workspace's ?tab= key is `contracts`. */
export const jobHref = (projectId: number) => `/projects/${projectId}?tab=contracts`;

// ── Arranging the rows ───────────────────────────────────────────────────────

export interface SubSection {
  key: string;
  bucket: ContractSubBucket;
  /** Set for the three "Waiting on sub" groups. */
  category: ContractWaitCategory | null;
  title: string;
  rows: ContractSub[];
}

/**
 * The sheet's headings, in the sheet's order, each holding its rows. A heading
 * with nothing under it is left out. "Waiting on sub" splits by group, and a sub
 * waiting with no group chosen gets a heading of its own rather than being lost.
 * Rows keep the order the API sent (job number, then subcontract number).
 */
export function groupSubs(rows: readonly ContractSub[]): SubSection[] {
  const out: SubSection[] = [];
  for (const bucket of BUCKET_ORDER) {
    const inBucket = rows.filter((r) => r.bucket === bucket);
    if (inBucket.length === 0) continue;
    if (bucket !== 'waiting_on_sub') {
      out.push({ key: bucket, bucket, category: null, title: BUCKET_LABELS[bucket], rows: inBucket });
      continue;
    }
    for (const category of [...WAIT_CATEGORY_ORDER, null] as (ContractWaitCategory | null)[]) {
      const rowsInGroup = inBucket.filter((r) => r.waitCategory === category);
      if (rowsInGroup.length === 0) continue;
      out.push({
        key: `waiting_on_sub:${category ?? 'none'}`,
        bucket,
        category,
        title: category
          ? `${BUCKET_LABELS.waiting_on_sub} (${WAIT_CATEGORY_LABELS[category]})`
          : `${BUCKET_LABELS.waiting_on_sub} (no group chosen)`,
        rows: rowsInGroup,
      });
    }
  }
  return out;
}

export interface SubCounts {
  /** Preparing, internal review, waiting on the sub, or signed and finalizing. */
  inProgress: number;
  onHold: number;
  /** Executed but a form is still out: the sub can't report to site. */
  missingItems: number;
  cleared: number;
  voided: number;
}

export function countSubs(rows: readonly ContractSub[]): SubCounts {
  const c: SubCounts = { inProgress: 0, onHold: 0, missingItems: 0, cleared: 0, voided: 0 };
  for (const r of rows) {
    if (r.bucket === 'on_hold') c.onHold++;
    else if (r.bucket === 'missing_items') c.missingItems++;
    else if (r.bucket === 'cleared') c.cleared++;
    else if (r.bucket === 'voided') c.voided++;
    else c.inProgress++;
  }
  return c;
}

/** Owner contracts still under review. */
export const countOpenOwners = (rows: readonly ContractOwner[]) => rows.filter((o) => o.completedOn === null).length;

const has = (v: string | null | undefined, q: string) => (v ?? '').toLowerCase().includes(q);

/** Case-insensitive match across what a person would search a subcontract by. */
export function matchesSub(r: ContractSub, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return [r.jobNumber, r.projectTitle, r.vendorName, r.sageDocNo, r.notes, TYPE_LABELS[r.agreementType]]
    .some((v) => has(v, q));
}

/** Case-insensitive match across what a person would search a subcontract request by. */
export function matchesRequest(r: SubcontractRequestListItem, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return [r.jobNumber, r.projectTitle, r.vendorName, r.subcontractNumber, r.requestedBy.name, r.templateLabel]
    .some((v) => has(v, q));
}

export function matchesOwner(o: ContractOwner, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return [o.name, o.sentBy, o.jobNumber, o.projectTitle, o.notes].some((v) => has(v, q));
}

// ── One row's display ────────────────────────────────────────────────────────

/** received = the form is in; missing = executed and still out (a problem); waiting = not due yet. */
export type ChipState = 'received' | 'missing' | 'waiting';

export interface FormChip {
  key: ContractFormKey;
  short: string;
  label: string;
  state: ChipState;
  /** YYYY-MM-DD it arrived, when it has. */
  on: string | null;
}

/**
 * The forms this subcontract needs, each with where it stands. A missing form is
 * only a problem once the contract is executed (the server files the row under
 * "missing items" then); before that it is just not due yet, and a voided row
 * raises no alarm at all.
 */
export function formChips(r: ContractSub): FormChip[] {
  const on: Record<ContractFormKey, string | null> = { cc: r.ccOn, nv: r.nvOn, w9: r.w9On, coi: r.coiOn };
  return r.required.map((key) => {
    const arrived = on[key];
    const state: ChipState = arrived ? 'received' : r.bucket === 'missing_items' ? 'missing' : 'waiting';
    return { key, short: FORM_SHORT[key], label: FORM_LABELS[key], state, on: arrived };
  });
}

/** The header of the section's date column, for the headings that have one. */
export const DATE_COLUMN_LABEL: Partial<Record<ContractSubBucket, string>> = {
  on_hold: 'On hold since',
  missing_items: 'Finalized',
  cleared: 'Cleared',
  voided: 'Voided',
};

/** The one date that matters for the heading a row sits under. */
export function contextDate(r: ContractSub): { label: string; on: string } | null {
  switch (r.bucket) {
    case 'on_hold': return r.holdOn ? { label: 'On hold since', on: r.holdOn } : null;
    case 'missing_items': return r.ccOn ? { label: 'Finalized', on: r.ccOn } : null;
    case 'cleared': return r.clearedOn ? { label: 'Cleared', on: r.clearedOn } : null;
    case 'voided': return r.voidedOn ? { label: 'Voided', on: r.voidedOn } : null;
    default: return null;
  }
}

// ── Forms → requests ─────────────────────────────────────────────────────────

const SUB_DATE_FIELDS = [
  ['originallySentOn', 'Originally sent'],
  ['holdOn', 'Hold date'],
  ['ccOn', 'Contract executed'],
  ['nvOn', 'New vendor form'],
  ['w9On', 'W9'],
  ['coiOn', 'Certificate of insurance'],
] as const;
type SubDateKey = (typeof SUB_DATE_FIELDS)[number][0];

/** Every field is a string but the two choices and the checkbox, as a form holds them. */
export interface SubForm {
  projectId: string;
  vendorName: string;
  agreementType: ContractAgreementType;
  sageDocNo: string;
  stage: ContractSubStage;
  waitCategory: '' | ContractWaitCategory;
  newVendor: boolean;
  originallySentOn: string;
  holdOn: string;
  ccOn: string;
  nvOn: string;
  w9On: string;
  coiOn: string;
  notes: string;
}

export const emptySubForm = (projectId = ''): SubForm => ({
  projectId, vendorName: '', agreementType: 'subcontract', sageDocNo: '', stage: 'preparing',
  waitCategory: '', newVendor: false, originallySentOn: '', holdOn: '', ccOn: '', nvOn: '',
  w9On: '', coiOn: '', notes: '',
});

export const subFormFrom = (r: ContractSub): SubForm => ({
  projectId: r.projectId === null ? '' : String(r.projectId),
  vendorName: r.vendorName,
  agreementType: r.agreementType,
  sageDocNo: r.sageDocNo ?? '',
  stage: r.stage,
  waitCategory: r.waitCategory ?? '',
  newVendor: r.newVendor,
  originallySentOn: r.originallySentOn ?? '',
  holdOn: r.holdOn ?? '',
  ccOn: r.ccOn ?? '',
  nvOn: r.nvOn ?? '',
  w9On: r.w9On ?? '',
  coiOn: r.coiOn ?? '',
  notes: r.notes ?? '',
});

const blankToNull = (s: string): string | null => (s.trim() === '' ? null : s.trim());

/**
 * The form after the stage is changed. Moving a sub onto hold fills in today as
 * the hold date when there is none — the usual case is "putting it on hold now" —
 * and leaves it editable; the API takes it blank too.
 *
 * Nothing else moves. In particular the hold date and the waiting group are NOT
 * cleared when the stage leaves them: the form keeps what it had, so stepping back
 * to that stage before saving restores it instead of reading as a deliberate
 * clear. `subCreateBody` and `subPatch` send each only while its own stage is the
 * one selected, and the API drops the old value itself when a row moves on.
 */
export function withStage(f: SubForm, stage: ContractSubStage, today: string): SubForm {
  const next: SubForm = { ...f, stage };
  if (stage === 'on_hold' && next.holdOn === '') next.holdOn = today;
  return next;
}

/**
 * Whether a date field takes part in the request. Every one does but the hold
 * date, which the API only accepts while the sub is on hold (a stale value left in
 * the form after switching stage must not be sent, or checked, or shown).
 */
const dateApplies = (key: SubDateKey, stage: ContractSubStage) => key !== 'holdOn' || stage === 'on_hold';

/**
 * What is wrong with one date on a form, in the words to show, or null (a blank date
 * is fine). Not after `today`, and not before the floor the date pickers already
 * carry (`DATE_INPUT_MIN`): the picker only flags a year typed as "26" — it is stored
 * as year 26 and shown as 1926 — and these screens save from a button, so nothing else
 * stops it. Mirrors the API, which refuses both.
 */
function dateProblem(label: string, value: string, today: string): string | null {
  if (!value) return null;
  if (value < DATE_INPUT_MIN) return `${label} can’t be before ${DATE_INPUT_MIN.slice(0, 4)}.`;
  if (value > today) return `${label} can’t be in the future.`;
  return null;
}

/**
 * A stand-in for "no number yet" — the sheet writes S00XXX into a row's label — is
 * not a number. Mirrors the API, which refuses it too; asking here saves the round trip.
 */
const DOC_NO_PLACEHOLDER = /X{2,}/i;
export const DOC_NO_PLACEHOLDER_MESSAGE =
  'That looks like a placeholder, not a subcontract number. Leave it blank until it has one.';

/**
 * Said when a new subcontract or PSA has no job. It names the way out, because the form opens
 * on "Subcontract" and nothing else says an MSA does not need one until the Type is changed.
 */
export const JOB_REQUIRED_MESSAGE = 'Pick the job. An MSA doesn’t need one — choose MSA as the type.';

/** The first thing wrong with the form, in the words to show, or null. `today` is YYYY-MM-DD. */
export function validateSubForm(f: SubForm, today: string, creating: boolean): string | null {
  // A blank job is a choice only for an MSA, which is not tied to one.
  if (creating && !f.projectId && !canGoWithoutJob(f.agreementType)) return JOB_REQUIRED_MESSAGE;
  if (!f.vendorName.trim()) return 'Say which vendor this is.';
  if (DOC_NO_PLACEHOLDER.test(f.sageDocNo)) return DOC_NO_PLACEHOLDER_MESSAGE;
  for (const [key, label] of SUB_DATE_FIELDS) {
    if (!dateApplies(key, f.stage)) continue;
    const problem = dateProblem(label, f[key], today);
    if (problem) return problem;
  }
  return null;
}

/**
 * A new subcontract: only what was actually filled in, so the server's defaults apply to the
 * rest. That includes the job: an MSA left on "No job" sends none.
 */
export function subCreateBody(f: SubForm): ContractSubInput & { vendorName: string } {
  const body: ContractSubInput & { vendorName: string } = {
    vendorName: f.vendorName.trim(),
    stage: f.stage,
  };
  if (f.projectId) body.projectId = Number(f.projectId);
  if (f.agreementType !== 'subcontract') body.agreementType = f.agreementType;
  const doc = blankToNull(f.sageDocNo);
  if (doc) body.sageDocNo = doc;
  if (f.stage === 'waiting_on_sub' && f.waitCategory) body.waitCategory = f.waitCategory;
  if (f.newVendor) body.newVendor = true;
  for (const [key] of SUB_DATE_FIELDS) if (dateApplies(key, f.stage) && f[key]) body[key] = f[key];
  const notes = blankToNull(f.notes);
  if (notes) body.notes = notes;
  return body;
}

/**
 * A change: ONLY the fields that differ from what the form was opened with.
 *
 * ⚠️ Not the whole form. The server merges a PATCH onto the row as it stands, so
 * sending just what this person changed means a second coordinator's edit, made
 * while this form was open, is not overwritten by a stale copy of a field nobody
 * here touched.
 *
 * The waiting group is sent only while the stage is "waiting on sub", and the hold
 * date only while it is "on hold": the server refuses either for any other stage,
 * and drops it itself when a sub moves on.
 */
export function subPatch(initial: SubForm, now: SubForm): Omit<ContractSubInput, 'projectId'> {
  const out: Omit<ContractSubInput, 'projectId'> = {};
  if (now.vendorName.trim() !== initial.vendorName.trim()) out.vendorName = now.vendorName.trim();
  if (now.agreementType !== initial.agreementType) out.agreementType = now.agreementType;
  if (now.sageDocNo.trim() !== initial.sageDocNo.trim()) out.sageDocNo = blankToNull(now.sageDocNo);
  if (now.stage !== initial.stage) out.stage = now.stage;
  if (now.stage === 'waiting_on_sub' && now.waitCategory !== initial.waitCategory) {
    out.waitCategory = now.waitCategory === '' ? null : now.waitCategory;
  }
  if (now.newVendor !== initial.newVendor) out.newVendor = now.newVendor;
  for (const [key] of SUB_DATE_FIELDS) {
    if (!dateApplies(key, now.stage)) continue;
    if (now[key as SubDateKey] !== initial[key as SubDateKey]) out[key] = now[key] === '' ? null : now[key];
  }
  if (now.notes.trim() !== initial.notes.trim()) out.notes = blankToNull(now.notes);
  return out;
}

// ── Owner contracts ──────────────────────────────────────────────────────────

const OWNER_DATE_FIELDS = [
  ['receivedOn', 'Received'],
  ['redlineSentOn', 'Redline sent'],
  ['approvedOn', 'Approved'],
  ['completedOn', 'Completed'],
] as const;

export interface OwnerForm {
  name: string;
  /** '' = no job yet. */
  projectId: string;
  sentBy: string;
  receivedOn: string;
  redlineSentOn: string;
  approvedOn: string;
  completedOn: string;
  notes: string;
}

export const emptyOwnerForm = (projectId = ''): OwnerForm => ({
  name: '', projectId, sentBy: '', receivedOn: '', redlineSentOn: '', approvedOn: '', completedOn: '', notes: '',
});

export const ownerFormFrom = (o: ContractOwner): OwnerForm => ({
  name: o.name,
  projectId: o.projectId === null ? '' : String(o.projectId),
  sentBy: o.sentBy ?? '',
  receivedOn: o.receivedOn ?? '',
  redlineSentOn: o.redlineSentOn ?? '',
  approvedOn: o.approvedOn ?? '',
  completedOn: o.completedOn ?? '',
  notes: o.notes ?? '',
});

export function validateOwnerForm(f: OwnerForm, today: string): string | null {
  if (!f.name.trim()) return 'Say which contract this is.';
  for (const [key, label] of OWNER_DATE_FIELDS) {
    const problem = dateProblem(label, f[key], today);
    if (problem) return problem;
  }
  return null;
}

export function ownerCreateBody(f: OwnerForm): ContractOwnerInput & { name: string } {
  const body: ContractOwnerInput & { name: string } = { name: f.name.trim() };
  if (f.projectId) body.projectId = Number(f.projectId);
  const sentBy = blankToNull(f.sentBy);
  if (sentBy) body.sentBy = sentBy;
  for (const [key] of OWNER_DATE_FIELDS) if (f[key]) body[key] = f[key];
  const notes = blankToNull(f.notes);
  if (notes) body.notes = notes;
  return body;
}

/** Only the fields that differ, for the same reason as `subPatch`. */
export function ownerPatch(initial: OwnerForm, now: OwnerForm): ContractOwnerInput {
  const out: ContractOwnerInput = {};
  if (now.name.trim() !== initial.name.trim()) out.name = now.name.trim();
  if (now.projectId !== initial.projectId) out.projectId = now.projectId === '' ? null : Number(now.projectId);
  if (now.sentBy.trim() !== initial.sentBy.trim()) out.sentBy = blankToNull(now.sentBy);
  for (const [key] of OWNER_DATE_FIELDS) {
    if (now[key] !== initial[key]) out[key] = now[key] === '' ? null : now[key];
  }
  if (now.notes.trim() !== initial.notes.trim()) out.notes = blankToNull(now.notes);
  return out;
}
